if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.gallery = this.querySelector('[data-gallery]');

        /** @type {HTMLElement | null} */
        this.scrollbar = this.querySelector('[data-gallery-scrollbar]');

        /** @type {HTMLElement | null} */
        this.next = this.querySelector('[data-gallery-next]');

        /** @type {HTMLElement | null} */
        this.prev = this.querySelector('[data-gallery-prev]');

        /** @type {import('swiper').Swiper | null} */
        this.instance = null;

        /** @type {HTMLImageElement[]} */
        this.images = Array.from(this.querySelectorAll('img'));

        /** @type {HTMLVideoElement[]} */
        this.videos = Array.from(this.querySelectorAll('video'));

        /** @type {HTMLElement | null} - Zoom icon shown when active slide is an image */
        this.zoomIcon = this.querySelector('.media-gallery-image-zoom-icon');

        /** @type {boolean} - Only sync from modal after user has opened it (avoids skipping video on mobile load) */
        this._zoomSyncFromModalAllowed = false;

        this._boundHandleZoomIndexChanged = this._handleZoomIndexChanged.bind(this);
        this._boundAllowZoomSync = this._allowZoomSyncFromModal.bind(this);
      }

      connectedCallback() {
        const swiper = window.swiper.Swiper;

        window.addEventListener('product-gallery:zoom-index-changed', this._boundHandleZoomIndexChanged);
        this.addEventListener('click', this._boundAllowZoomSync, true);

        if (this.gallery && this.scrollbar) {
          this.instance = new swiper(this.gallery, {
            modules: [window.swiper.Scrollbar, window.swiper.Navigation],
            cssMode: true,
            slidesPerView: 1,
            spaceBetween: 0,
            scrollbar: {
              el: this.scrollbar,
              hide: false,
            },
            navigation: {
              nextEl: this.next,
              prevEl: this.prev,
            },
            on: {
              init: (s) => {
                this._updateZoomIconVisibility(s);
              },
              resize: (s) => {
                if (window.innerWidth >= 1200) {
                  s.slideTo(0);
                  s.disable();
                } else {
                  s.enable();
                }
              },
              slideChange: (s) => {
                this._updateZoomIconVisibility(s);

                window.gtag('event', 'media_gallery_slide_change', {
                  slide_index: s.activeIndex + 1,
                  page_path_clean: window.location.pathname.replace(/\/$/, ''),
                });

                this.images.forEach((image) => {
                  if (image) image.setAttribute('loading', 'eager');
                });

                this.videos.forEach((video) => {
                  if (video && video.getAttribute('preload') !== 'metadata') {
                    video.setAttribute('preload', 'metadata');
                  }
                });

                const video = s.slides[s.activeIndex].querySelector('video');

                if (video) {
                  video.muted = true;
                  video.play();
                }
              },
            },
          });
        }
      }

      /**
       * Show or hide the zoom icon based on whether the active slide is an image.
       * @param {import('swiper').Swiper} swiper - The Swiper instance.
       * @returns {void}
       */
      _updateZoomIconVisibility(swiper) {
        if (!this.zoomIcon) return;

        const activeSlide = swiper.slides?.[swiper.activeIndex];
        const isImageSlide = activeSlide?.getAttribute('data-slide-type') === 'image';

        this.zoomIcon.style.opacity = isImageSlide ? '1' : '0';
      }

      disconnectedCallback() {
        window.removeEventListener('product-gallery:zoom-index-changed', this._boundHandleZoomIndexChanged);
        this.removeEventListener('click', this._boundAllowZoomSync, true);

        if (this.instance) {
          this.instance.destroy(true, true);
          this.instance = null;
        }
      }

      /**
       * Allow main gallery to sync from modal after user opens it (e.g. tap on image slide on mobile).
       * @param {Event} e - The click event
       */
      _allowZoomSyncFromModal(e) {
        const trigger =
          e.target instanceof Element ? e.target.closest('[data-focus-trap-toggle="product-gallery-zoom"]') : null;
        if (trigger && this.contains(trigger)) this._zoomSyncFromModalAllowed = true;
      }

      /** @param {any} e - The product-gallery:zoom-index-changed event (detail.index is the zoom index) */
      _handleZoomIndexChanged(e) {
        if (!this._zoomSyncFromModalAllowed) return;

        const index = e?.detail?.index;
        if (typeof index !== 'number') return;
        if (!this.instance) return;

        const slides = Array.from(this.instance.slides ?? []);
        const targetSlide = slides.find((slide) => {
          const raw = slide.getAttribute('data-zoom-index');
          if (raw == null) return false;
          return parseInt(raw, 10) === index;
        });

        if (!targetSlide) return;

        const slideIndex = slides.indexOf(targetSlide);
        if (slideIndex < 0) return;

        this.instance.slideTo(slideIndex, 0);
      }
    },
  );
}
